(function () {
  'use strict';

  const STORAGE_KEYS = Object.freeze({
    sessionId: 'taraConsultationSessionId',
    sessionIdAlias: 'tara_session_id',
    history: 'taraConsultationHistory',
    conversationHistory: 'taraConsultationConversationHistory',
    lastQuestionId: 'taraConsultationLastQuestionId',
    startedAt: 'taraConsultationStartedAt',
    assessment: 'taraConsultationAssessment',
    onboardingSeen: 'taraConsultationOnboardingSeen',
  });

  function getSessionItem(key, fallback) {
    try {
      const value = sessionStorage.getItem(key);
      return value == null ? fallback : value;
    } catch (error) {
      return fallback;
    }
  }

  function setSessionItem(key, value) {
    try {
      sessionStorage.setItem(key, value);
      return true;
    } catch (error) {
      return false;
    }
  }

  function removeSessionItem(key) {
    try {
      sessionStorage.removeItem(key);
      return true;
    } catch (error) {
      return false;
    }
  }

  function parseJson(rawValue, fallback) {
    if (!rawValue) return fallback;
    try {
      return JSON.parse(rawValue);
    } catch (error) {
      return fallback;
    }
  }

  function getStoredSessionId() {
    return getSessionItem(STORAGE_KEYS.sessionId, null) || getSessionItem(STORAGE_KEYS.sessionIdAlias, null);
  }

  function setStoredSessionId(sessionId) {
    if (!sessionId) return false;
    const primaryStored = setSessionItem(STORAGE_KEYS.sessionId, sessionId);
    setSessionItem(STORAGE_KEYS.sessionIdAlias, sessionId);
    return primaryStored;
  }

  function ensureSessionId(currentSessionId) {
    const resolved = currentSessionId
      || getStoredSessionId()
      || (crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(36) + Math.random().toString(36).slice(2));

    setStoredSessionId(resolved);
    return resolved;
  }

  function getConversationHistory() {
    return parseJson(getSessionItem(STORAGE_KEYS.conversationHistory, ''), []) || [];
  }

  function setConversationHistory(history) {
    const serialized = JSON.stringify(Array.isArray(history) ? history : []);
    setSessionItem(STORAGE_KEYS.history, serialized);
    return setSessionItem(STORAGE_KEYS.conversationHistory, serialized);
  }

  function getStartedAt() {
    return getSessionItem(STORAGE_KEYS.startedAt, '');
  }

  function ensureStartedAt() {
    const existing = getStartedAt();
    if (existing) return existing;
    const startedAt = new Date().toISOString();
    setSessionItem(STORAGE_KEYS.startedAt, startedAt);
    return startedAt;
  }

  function getLastQuestionId() {
    return getSessionItem(STORAGE_KEYS.lastQuestionId, '');
  }

  function setLastQuestionId(questionId) {
    if (!questionId) return false;
    return setSessionItem(STORAGE_KEYS.lastQuestionId, String(questionId));
  }

  function setProgressState(state) {
    const sessionId = state && state.sessionId;
    const history = Array.isArray(state && state.history) ? state.history : [];
    const lastQuestionId = state && state.lastQuestionId;

    if (sessionId) setStoredSessionId(sessionId);
    setConversationHistory(history);
    if (lastQuestionId) setLastQuestionId(lastQuestionId);
    ensureStartedAt();
  }

  function getProgressState(maxAgeMs) {
    const sessionId = getStoredSessionId();
    const history = parseJson(getSessionItem(STORAGE_KEYS.history, '') || getSessionItem(STORAGE_KEYS.conversationHistory, ''), []) || [];
    const startedAt = getStartedAt();
    const startedTime = startedAt ? new Date(startedAt).getTime() : 0;
    const isFresh = Boolean(
      sessionId
      && history.length
      && startedTime
      && (!maxAgeMs || Date.now() - startedTime < maxAgeMs)
    );

    if (!isFresh) return null;

    return {
      sessionId,
      history,
      lastQuestionId: getLastQuestionId(),
      startedAt,
    };
  }

  function readAssessmentRecord() {
    return parseJson(getSessionItem(STORAGE_KEYS.assessment, ''), null);
  }

  function getStoredAssessment() {
    const parsed = readAssessmentRecord();
    if (!parsed) return null;
    if (parsed && typeof parsed === 'object' && parsed.assessment) return parsed.assessment;
    return parsed;
  }

  function setStoredAssessment(record) {
    return setSessionItem(STORAGE_KEYS.assessment, JSON.stringify(record));
  }

  function clearProgressState() {
    removeSessionItem(STORAGE_KEYS.sessionId);
    removeSessionItem(STORAGE_KEYS.sessionIdAlias);
    removeSessionItem(STORAGE_KEYS.history);
    removeSessionItem(STORAGE_KEYS.conversationHistory);
    removeSessionItem(STORAGE_KEYS.lastQuestionId);
    removeSessionItem(STORAGE_KEYS.startedAt);
  }

  function clearStoredState() {
    clearProgressState();
    removeSessionItem(STORAGE_KEYS.assessment);
  }

  function hasSeenOnboarding() {
    try {
      return localStorage.getItem(STORAGE_KEYS.onboardingSeen) === '1';
    } catch (error) {
      return false;
    }
  }

  function setOnboardingSeen() {
    try {
      localStorage.setItem(STORAGE_KEYS.onboardingSeen, '1');
      return true;
    } catch (error) {
      return false;
    }
  }

  window.TaraConsultationSession = Object.freeze({
    keys: STORAGE_KEYS,
    getStoredSessionId,
    setStoredSessionId,
    ensureSessionId,
    getConversationHistory,
    setConversationHistory,
    getStartedAt,
    getLastQuestionId,
    setLastQuestionId,
    setProgressState,
    getProgressState,
    readAssessmentRecord,
    getStoredAssessment,
    setStoredAssessment,
    clearProgressState,
    clearStoredState,
    hasSeenOnboarding,
    setOnboardingSeen,
  });
})();
